import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch, ApiError } from '../api/client'

// QrResponse is the locked console-api contract for
// GET /v1/glows/{id}/qr?format=json. The console apiFetch only decodes
// application/json, so the QR is fetched as base64 (not raw PNG bytes) and
// rendered client-side as a `data:image/png;base64,...` URL.
export interface QrResponse {
  png_base64: string
  content_type: string
  url: string
}

// useGlowQr fetches the branded QR PNG for a glow as base64. Modeled on
// useGlowUsage, but additionally gated on `enabled` so the QR is only fetched
// when the modal is open (mount-on-open) — no eager fetch on every
// glow-detail view. The empty-png_base64 guard is defensive per the plan's R1
// (a malformed/empty payload surfaces as an error state rather than a broken
// <img>).
export function useGlowQr(glowID: string, enabled: boolean) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['glow-qr', glowID],
    enabled: !!user && !!glowID && enabled,
    queryFn: async ({ signal }) => {
      const res = await apiFetch<QrResponse>(
        `/v1/glows/${glowID}/qr?format=json`,
        idTokenProvider,
        { signal },
      )
      if (!res?.png_base64) {
        throw new ApiError(0, 'internal', 'QR code generation returned no image.')
      }
      return res
    },
  })
}
